import { createClient, SupabaseClient } from '@supabase/supabase-js'

/**
 * Supabase Client Setup
 * Singleton instance for the entire application
 */

// Check if we're in Electron or Web environment
const isElectron = typeof window !== 'undefined' && 'api' in window && window.api !== undefined

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('[Supabase] Missing environment variables. Cloud features will be disabled.')
  console.warn('[Supabase] Make sure .env file exists with VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY')
}

// Create Supabase client
let supabaseClient: SupabaseClient | null = null

export function getSupabaseClient(): SupabaseClient | null {
  // Return null if no credentials (Offline mode in Electron)
  if (!supabaseUrl || !supabaseAnonKey) {
    return null
  }

  // Create singleton instance
  if (!supabaseClient) {
    supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        // Storage adapter for session persistence
        // Electron: uses OS keychain encryption via safeStorage IPC
        // Web: uses localStorage
        storage: {
          getItem: async (key: string) => {
            if (isElectron && window.api?.invoke) {
              const encrypted = await window.api.invoke('safe-storage-get', key)
              if (encrypted !== null) return encrypted
              // Fallback: migrate from localStorage
              const local = localStorage.getItem(key)
              if (local) {
                await window.api.invoke('safe-storage-set', key, local)
                localStorage.removeItem(key)
              }
              return local
            }
            return localStorage.getItem(key)
          },
          setItem: async (key: string, value: string) => {
            if (isElectron && window.api?.invoke) {
              await window.api.invoke('safe-storage-set', key, value)
              return
            }
            localStorage.setItem(key, value)
          },
          removeItem: async (key: string) => {
            if (isElectron && window.api?.invoke) {
              await window.api.invoke('safe-storage-remove', key)
              localStorage.removeItem(key) // Also clean up legacy
              return
            }
            localStorage.removeItem(key)
          },
        },
      },
      realtime: {
        worker: typeof SharedWorker !== 'undefined',
        heartbeatIntervalMs: 15_000,
        reconnectAfterMs: (tries: number) =>
          [500, 1000, 2000, 5000, 10_000][Math.min(tries, 4)],
      },
    })
  }

  return supabaseClient
}

// Export singleton
export const supabase = getSupabaseClient()

// Module-level subscription for PASSWORD_RECOVERY — registered before React mounts
// so we never miss the event. Supabase SDK processes the URL (PKCE ?code= or
// implicit #access_token=) asynchronously right after createClient, and a React
// useEffect subscription in AuthProvider may be too late for that first emit.
if (supabase && typeof window !== 'undefined') {
  supabase.auth.onAuthStateChange((event) => {
    if (event === 'PASSWORD_RECOVERY') {
      localStorage.setItem('passwordRecoveryMode', 'true')
    }
  })
}

// Helper: Check if Supabase is available
export function isSupabaseAvailable(): boolean {
  return supabase !== null
}

// Helper: Get current user ID (from cached session — no network call)
export async function getCurrentUserId(): Promise<string | null> {
  if (!supabase) return null

  const {
    data: { session },
  } = await supabase.auth.getSession()
  return session?.user?.id || null
}
